import type { AccountBundleV1, AttachmentManifestV1 } from './types';
import { ATTACHMENT_PLAINTEXT_BLOCK_BYTES, MAX_ATTACHMENT_BYTES } from './types';
import { aesGcmDecrypt, aesGcmEncrypt, blockNonce, hkdf, randomBytes, sha256, sign, verify } from './primitives';
import { base64url, canonicalBytes, unbase64url, withoutSignature, type Binary } from './encoding';

function blockAad(input: { attachmentID: string; holdID: string; ownerID: string; uploadRevision: number; blockIndex: number; blockCount: number; plaintextBytes: number }) {
  return canonicalBytes({ cryptoVersion: 1, ...input });
}

export async function attachmentKey(entityKey: Binary, attachmentID: string) {
  return hkdf(entityKey, `holdthat:attachment-key:v1:${attachmentID}`);
}

export async function encryptAttachmentToSink(
  bundle: AccountBundleV1,
  input: { blob: Blob; attachmentID: string; holdID: string; ownerID: string; uploadRevision: number; entityKey: Binary },
  writeCiphertextBlock: (index: number, ciphertext: Uint8Array<ArrayBuffer>) => Promise<void>,
): Promise<AttachmentManifestV1> {
  if (input.blob.size > MAX_ATTACHMENT_BYTES) throw new Error('Anhänge dürfen höchstens 100 MiB groß sein.');
  const key = await attachmentKey(input.entityKey, input.attachmentID); const noncePrefix = randomBytes(8);
  const blockCount = Math.max(1, Math.ceil(input.blob.size / ATTACHMENT_PLAINTEXT_BLOCK_BYTES));
  const blockDigests: string[] = []; let ciphertextBytes = 0;
  for (let blockIndex = 0; blockIndex < blockCount; blockIndex++) {
    const start = blockIndex * ATTACHMENT_PLAINTEXT_BLOCK_BYTES;
    const plaintext = new Uint8Array(await input.blob.slice(start, Math.min(input.blob.size, start + ATTACHMENT_PLAINTEXT_BLOCK_BYTES)).arrayBuffer());
    const nonce = blockNonce(noncePrefix, blockIndex);
    const sealed = await aesGcmEncrypt(key, plaintext, blockAad({
      attachmentID: input.attachmentID, holdID: input.holdID, ownerID: input.ownerID,
      uploadRevision: input.uploadRevision, blockIndex, blockCount, plaintextBytes: plaintext.length,
    }), nonce);
    blockDigests.push(base64url(await sha256(sealed.ciphertext))); ciphertextBytes += sealed.ciphertext.length;
    await writeCiphertextBlock(blockIndex, sealed.ciphertext);
  }
  const signable = {
    cryptoVersion: 1 as const, attachmentID: input.attachmentID, holdID: input.holdID, ownerID: input.ownerID,
    uploadRevision: input.uploadRevision, blockCount, ciphertextBytes, noncePrefix: base64url(noncePrefix), blockDigests,
    signerUserID: input.ownerID, signerKeyRevision: bundle.keyRevision,
  };
  return { ...signable, signature: await sign(bundle, signable) };
}

export async function decryptAttachmentBlock(
  manifest: AttachmentManifestV1, index: number, ciphertext: Binary, entityKey: Binary, signerPublicKey: string,
) {
  if (!await verify(signerPublicKey, withoutSignature(manifest as unknown as Record<string, unknown>), manifest.signature)) throw new Error('Attachment-Manifest-Signatur ist ungültig.');
  if (index < 0 || index >= manifest.blockCount) throw new Error('Ungültiger Attachment-Blockindex.');
  const digest = base64url(await sha256(ciphertext)); if (digest !== manifest.blockDigests[index]) throw new Error('Attachment-Block wurde manipuliert.');
  const key = await attachmentKey(entityKey, manifest.attachmentID);
  return aesGcmDecrypt(key, blockNonce(unbase64url(manifest.noncePrefix, 8), index), ciphertext, blockAad({ attachmentID: manifest.attachmentID, holdID: manifest.holdID, ownerID: manifest.ownerID, uploadRevision: manifest.uploadRevision, blockIndex: index, blockCount: manifest.blockCount, plaintextBytes: Math.max(0, ciphertext.byteLength - 16) }));
}
